// Resolves PI commodity names to CCP type_ids and renders icons via the
// official EVE image service.
//
// CCP image service (https://images.evetech.net) is CDN-friendly and we are
// allowed to point clients directly at it.
import { useEffect, useState } from "react";

export const EVE_IMAGE_SERVICE = "https://images.evetech.net";

let cachedTypeIds: Record<string, number> | null = null;
let inflight: Promise<Record<string, number>> | null = null;

async function fetchTypeIds(): Promise<Record<string, number>> {
  if (cachedTypeIds) return cachedTypeIds;
  if (inflight) return inflight;
  inflight = (async () => {
    const response = await fetch("/api/static/pi-types");
    if (!response.ok) {
      throw new Error(`Failed to load PI type IDs: HTTP ${response.status}`);
    }
    const payload = (await response.json()) as { type_ids: Record<string, number> };
    cachedTypeIds = payload.type_ids;
    return cachedTypeIds;
  })().finally(() => {
    inflight = null;
  });
  return inflight;
}

export function useTypeIds(): Record<string, number> | null {
  const [map, setMap] = useState<Record<string, number> | null>(cachedTypeIds);

  useEffect(() => {
    if (cachedTypeIds) {
      setMap(cachedTypeIds);
      return;
    }
    let cancelled = false;
    fetchTypeIds()
      .then((value) => {
        if (!cancelled) setMap(value);
      })
      .catch(() => {
        // Icons are optional; if the lookup fails we just skip rendering them.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return map;
}

// CCP image service only accepts these discrete sizes; any other value
// returns HTTP 400. We snap the desired size up to the nearest one.
const ALLOWED_IMAGE_SIZES = [32, 64, 128, 256, 512, 1024] as const;

function snapImageSize(desired: number): number {
  for (const size of ALLOWED_IMAGE_SIZES) {
    if (size >= desired) return size;
  }
  return ALLOWED_IMAGE_SIZES[ALLOWED_IMAGE_SIZES.length - 1];
}

export function iconUrlForTypeId(typeId: number, size = 64): string {
  return `${EVE_IMAGE_SERVICE}/types/${typeId}/icon?size=${snapImageSize(size)}`;
}

interface ProductIconProps {
  name: string;
  size?: number;
  typeIds?: Record<string, number> | null;
}

/**
 * Renders the EVE icon for a PI commodity by name. Renders nothing when the
 * type-id map hasn't loaded yet or the name is unknown so callers can use it
 * inline next to a label without conditional logic.
 */
export function ProductIcon({ name, size = 24, typeIds }: ProductIconProps) {
  const map = typeIds ?? cachedTypeIds;
  if (!map) return null;
  const typeId = map[name];
  if (typeId === undefined) return null;
  // Request 2x the rendered size for retina displays, snapped to an allowed value.
  const requestSize = snapImageSize(size * 2);
  return (
    <img
      src={iconUrlForTypeId(typeId, requestSize)}
      alt=""
      width={size}
      height={size}
      loading="lazy"
      className="product-icon"
    />
  );
}
